import { createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { fetchUserStats } from './thunks/fetchUserStats.js';
import { fetchUserPosts } from './thunks/fetchUserPosts.js';
import { subscribeToUserProfile } from './thunks/subscribeToUserProfile.js';
import { unsubscribeFromUserProfile } from './thunks/unsubscribeFromUserProfile.js';
import { updateProfileOptimistic } from './thunks/updateProfileOptimistic.js';

const initialState = {
  userProfileData: null,
  postCount: 0,
  followingCount: 0,
  posts: [],
  activeTab: null,
  isLoading: false,
  error: null,
  postsLoading: false,
  postsError: null,
  lastFetched: null,
  lastUpdated: null,
  postsPage: 0,
  postsPerPage: 10,
  hasMorePosts: false,
  // Real-time subscription state
  realtimeSubscription: null,
  realtimeError: null,
  isSubscribed: false,
  // Optimistic updates
  optimisticUpdates: null,
  hasOptimisticUpdate: false,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    clearProfileError: (state) => {
      state.error = null;
      state.postsError = null;
    },
    setProfileLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPostsLoading: (state, action) => {
      state.postsLoading = action.payload;
    },
    resetProfileState: (state) => {
      // Clear subscription before reset
      if (state.realtimeSubscription?.unsubscribe) {
        state.realtimeSubscription.unsubscribe();
      }
      return initialState;
    },
    // Real-time subscription reducers
    setRealtimeSubscription: (state, action) => {
      state.realtimeSubscription = action.payload;
      state.isSubscribed = !!action.payload;
    },
    clearRealtimeSubscription: (state) => {
      state.realtimeSubscription = null;
      state.isSubscribed = false;
      state.realtimeError = null;
    },
    setRealtimeError: (state, action) => {
      state.realtimeError = action.payload;
    },
    updateUserProfileRealtime: (state, action) => {
      const userData = action.payload;
      state.userProfileData = userData;
      // Don't reset postCount from real-time updates unless it's explicitly provided
      if (userData?.postCount !== undefined) {
        state.postCount = userData.postCount;
      }
      state.followingCount = userData?.following?.length || 0;
      state.lastUpdated = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    },
    // Optimistic update reducers
    applyOptimisticUpdate: (state, action) => {
      state.optimisticUpdates = action.payload;
      state.hasOptimisticUpdate = true;
      // Apply updates to current data
      if (state.userProfileData) {
        state.userProfileData = { ...state.userProfileData, ...action.payload };
      }
    },
    revertOptimisticUpdate: (state) => {
      if (state.hasOptimisticUpdate && state.optimisticUpdates) {
        // Revert the optimistic changes
        // This is a simplified revert - in production you'd want to store the previous state
        state.hasOptimisticUpdate = false;
        state.optimisticUpdates = null;
      }
    },
    confirmOptimisticUpdate: (state) => {
      state.hasOptimisticUpdate = false;
      state.optimisticUpdates = null;
    },
  },
  extraReducers: (builder) => {
    // fetchUserStats
    builder
      .addCase(fetchUserStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.userProfileData = payload; // Store the entire payload
        state.postCount = payload?.postCount || 0;
        state.followingCount = payload?.following?.length || 0;
        state.lastFetched = new Date().toISOString();
        state.lastUpdated = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      })
      .addCase(fetchUserStats.rejected, (state, { payload, error }) => {
        state.isLoading = false;
        state.error = payload || error?.message || 'Failed to fetch user stats';
        state.lastFetched = new Date().toISOString();
        console.error('Profile stats error:', error || payload);
      });

    // fetchUserPosts
    builder
      .addCase(fetchUserPosts.pending, (state) => {
        state.postsLoading = true;
        state.postsError = null;
      })
      .addCase(fetchUserPosts.fulfilled, (state, { payload }) => {
        state.postsLoading = false;
        state.posts = Array.isArray(payload) ? payload : [];
        state.hasMorePosts = payload?.length >= (state.postsPerPage || 10);
        if (payload?.length > 0) {
          state.postsPage = (state.postsPage || 0) + 1;
        }
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchUserPosts.rejected, (state, { payload, error }) => {
        state.postsLoading = false;
        state.postsError = payload || error?.message || 'Failed to fetch user posts';
        state.lastFetched = new Date().toISOString();
        console.error('Profile posts error:', error || payload);
      })

      // subscribeToUserProfile
      .addCase(subscribeToUserProfile.pending, (state) => {
        state.realtimeError = null;
      })
      .addCase(subscribeToUserProfile.fulfilled, (state, { payload }) => {
        state.realtimeSubscription = payload;
        state.isSubscribed = true;
        state.realtimeError = null;
      })
      .addCase(subscribeToUserProfile.rejected, (state, { payload, error }) => {
        state.realtimeError = payload || error?.message || 'Failed to subscribe to user profile';
        state.isSubscribed = false;
        console.error('Real-time subscription error:', error || payload);
      })

      // unsubscribeFromUserProfile
      .addCase(unsubscribeFromUserProfile.fulfilled, (state) => {
        // Subscription already cleared in the thunk
      })
      .addCase(unsubscribeFromUserProfile.rejected, (state, { payload, error }) => {
        state.realtimeError = payload || error?.message || 'Failed to unsubscribe';
        console.error('Unsubscribe error:', error || payload);
      })

      // updateProfileOptimistic
      .addCase(updateProfileOptimistic.pending, (state) => {
        // Optimistic update already applied in the thunk
      })
      .addCase(updateProfileOptimistic.fulfilled, (state) => {
        state.confirmOptimisticUpdate();
      })
      .addCase(updateProfileOptimistic.rejected, (state, { payload, error }) => {
        state.error = payload || error?.message || 'Failed to update profile';
        console.error('Optimistic update error:', error || payload);
      });
  },
});

export const {
  setActiveTab,
  clearProfileError,
  resetProfileState,
  setProfileLoading,
  setPostsLoading,
  setRealtimeSubscription,
  clearRealtimeSubscription,
  setRealtimeError,
  updateUserProfileRealtime,
  applyOptimisticUpdate,
  revertOptimisticUpdate,
  confirmOptimisticUpdate
} = profileSlice.actions;

export default profileSlice.reducer;
